import React, { useState, useMemo } from 'react';
import { Fragment } from 'react';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import Search from './Search';
import FormattedDate from './FormattedDate';

// Helper to format ISO datetime to HH:mm
// timeZone is pinned: this table is server-rendered (getStaticProps), and the
// build/ISR server runs in UTC, not Europe/Copenhagen
const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Copenhagen' });
};

// reminderBell adds a bell column deep-linking each flight into the
// /when-to-arrive planner (?flight=...), which auto-looks it up. Only
// departures passes it - a leave-by reminder makes no sense for arrivals.
const PageFlightTable = ({ flights, from, to, title, reminderBell = false }) => {
    const { t, lang } = useTranslation('common');

    const [searchInput, setSearchInput] = useState('');
    const handleSearchChange = (value) => {
        setSearchInput(value);
    };

    const filteredFlights = useMemo(() => {
        if (!flights || !flights.length) {
            return [];
        }

        // Filter flights based on the search input
        return flights.filter(
            (flight) =>
                flight.Destination.toLowerCase().includes(
                    searchInput.toLowerCase()
                ) ||
                flight.Iata.toLowerCase().includes(searchInput.toLowerCase()) ||
                flight.Airline.toLowerCase().includes(searchInput.toLowerCase())
        );
    }, [flights, searchInput]);

    // if flights is a string, return nothing
    if (typeof flights === 'string') return null;
    if (!flights || flights.length === 0)
        return (
            <div className="flex grow mb-10 justify-center">
                <div className="flight-table mt-10 text-left text-black bg-neutral-100 p-5 rounded w-full max-w-screen-lg overflow-y-auto h-fit max-h-[50vh]">
                    <div className="font-semibold text-center">
                        {t('common:no_flights')}
                    </div>
                </div>
            </div>
        );

    return (
        <>
            <div className="p-4 flex flex-col">
                <div>
                    <div className="md:text-xl">{t(title)}</div>
                    <div className="text-xs md:text-s text-gray-500">
                        <FormattedDate date={from} locale={lang} /> -{' '}
                        <FormattedDate date={to} locale={lang} />
                    </div>
                </div>
                <Search onChange={handleSearchChange} />
            </div>

            <div className="w-full">
                <table className="table-auto w-full text-xs md:text-base text-left divide-x-2">
                    <thead className="bg-slate-100">
                        <tr className="*:px-2 *:border-b-4 *:border-r-2 *:py-4">
                            <th>Time</th>
                            <th>Destination</th>
                            <th className="hidden md:table-cell">Airline</th>
                            <th className="hidden md:table-cell">Flight No.</th>
                            <th>Gate</th>
                            <th>Status</th>
                            {reminderBell && (
                                <th>
                                    <span className="sr-only">
                                        {t('common:reminder_bell_label')}
                                    </span>
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredFlights.map((flight, index) => {
                            const [place, airport] =
                                flight.DestinationName.split('-');
                            return (
                                <Fragment key={`flight-rows-${index}`}>
                                    <tr
                                        key={`desktop-${index}`}
                                        onClick={() => {
                                            window.open(
                                                `https://www.cph.dk/flyinformation/afgange/${flight.Iata}?id=${flight.FlightId}`,
                                                '_blank'
                                            );
                                        }}
                                        tabIndex="0" // Enable keyboard focus
                                        role="button" // Indicate interactive element
                                        aria-label={`Flight details for ${place} ${airport}, departing at ${formatTime(flight.ScheduledDateTime)} with ${flight.Airline}`}
                                        className="*:border-b-2 *:py-2 *:px-2 even:bg-slate-50 *:border-r-2"
                                    >
                                        <td>
                                            <div
                                                className={`font-light ${
                                                    flight.ExpectedDateTime !==
                                                    flight.ScheduledDateTime
                                                        ? 'line-through'
                                                        : ''
                                                } whitespace-nowrap w-min`}
                                            >
                                                {formatTime(flight.ScheduledDateTime)}
                                            </div>
                                            <div>
                                                {flight.ExpectedDateTime !==
                                                flight.ScheduledDateTime
                                                    ? formatTime(flight.ExpectedDateTime)
                                                    : ''}
                                            </div>
                                        </td>
                                        <td>
                                            <div>{flight.Destination}</div>
                                            <div className="md:hidden">
                                                {flight.Iata}
                                            </div>
                                        </td>
                                        <td className="hidden md:table-cell">
                                            <div>{flight.Airline}</div>
                                        </td>
                                        <td className="hidden md:table-cell">
                                            <div>{flight.Iata}</div>
                                        </td>
                                        <td>
                                            <div>{flight.Gate}</div>
                                        </td>
                                        <td>
                                            <div>{flight.Status}</div>
                                        </td>
                                        {reminderBell && (
                                            <td className="text-center">
                                                <Link
                                                    href={`/when-to-arrive?flight=${encodeURIComponent(flight.Iata)}`}
                                                    onClick={(e) => {
                                                        // the whole row opens cph.dk in a new tab
                                                        e.stopPropagation();
                                                        if (typeof umami !== 'undefined') {
                                                            umami.track('departures-reminder-bell');
                                                        }
                                                    }}
                                                    title={`${t('common:reminder_bell_label')} (${flight.Iata})`}
                                                    aria-label={`${t('common:reminder_bell_label')} (${flight.Iata})`}
                                                    className="inline-block p-1 text-gray-400 hover:text-[var(--secondary)]"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={1.5}
                                                        stroke="currentColor"
                                                        className="w-5 h-5"
                                                        aria-hidden="true"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
                                                        />
                                                    </svg>
                                                </Link>
                                            </td>
                                        )}
                                    </tr>
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default PageFlightTable;
